import { useEffect, useRef, useState } from 'react'
import './holdButton.css'

/**
 * A button that does one thing on a click and another when held (Huanrui,
 * 2026-10-07): Copy to Project on a click, and, held, a new project made
 * with it -- what "Create a project" used to do.
 *
 * Held, the button fills from left to right like a progress bar; when it is
 * full the hold happens. Let go before that and it empties again: a quick
 * press (under QUICK ms) is the click, and a longer one that is let go early
 * does nothing, because it was a hold that changed its mind.
 *
 * The hold fires while the press is still recent, so a page that opens a tab
 * from it is still answering a person's press, which browsers allow.
 */

const QUICK = 300

export function HoldButton({
  className,
  onClick,
  onHold,
  holdMs = 1000,
  disabled,
  title,
  children,
}: {
  className: string
  onClick: () => void
  onHold: () => void
  holdMs?: number
  disabled?: boolean
  title?: string
  children: React.ReactNode
}) {
  const [holding, setHolding] = useState(false)
  const timer = useRef(0)
  const startedAt = useRef(0)
  const fired = useRef(false)

  useEffect(() => () => window.clearTimeout(timer.current), [])

  const stop = () => {
    window.clearTimeout(timer.current)
    setHolding(false)
  }

  return (
    <button
      type="button"
      className={`${className} hold${holding ? ' hold--on' : ''}`}
      style={{ '--hold-ms': `${holdMs}ms` } as React.CSSProperties}
      disabled={disabled}
      title={title}
      onPointerDown={(event) => {
        if (event.button !== 0 || disabled) return
        fired.current = false
        startedAt.current = Date.now()
        setHolding(true)
        window.clearTimeout(timer.current)
        timer.current = window.setTimeout(() => {
          fired.current = true
          setHolding(false)
          onHold()
        }, holdMs)
      }}
      onPointerUp={() => {
        if (fired.current) return
        const quick = Date.now() - startedAt.current < QUICK
        stop()
        if (quick) onClick()
      }}
      onPointerLeave={() => {
        if (!fired.current) stop()
      }}
      onPointerCancel={stop}
      // Keyboard: Enter or Space is the click. Pointer presses are handled above.
      onClick={(event) => {
        if (event.detail === 0) onClick()
      }}
    >
      <span className="hold__fill" aria-hidden="true" />
      <span className="hold__label">{children}</span>
    </button>
  )
}
